<template>
	<div class="g-block-pay-audio-library" :class="{
		'g-block-pay-audio-library--edit': editable,
		'is-active': editable && active,
		'g-block-pay-audio-library--h5': h5,
		'g-block-pay-audio-library--compact': compact,
		'g-block-pay-audio-library--unlocked': isUnlocked,
	}" @click="onWrapClick">
		<div class="g-block-pay-audio-library__card">
			<div class="g-block-pay-audio-library__header">
				<div class="g-block-pay-audio-library__header-main">
					<div class="g-block-pay-audio-library__title">{{ libraryData.title }}</div>
					<div class="g-block-pay-audio-library__subtitle">{{ subtitleText }}</div>
				</div>
				<div v-if="!isUnlocked && !shouldHidePrice" class="g-block-pay-audio-library__header-action">
					<div class="g-block-pay-audio-library__price">
						<span class="g-block-pay-audio-library__currency">{{ libraryData.currency }}</span>
						<span class="g-block-pay-audio-library__price-value">{{ libraryData.price }}</span>
					</div>
					<button type="button" class="g-block-pay-audio-library__unlock-btn" :style="buttonStyle"
						@click.stop="onUnlockClick">{{ libraryData.buttonText }}</button>
				</div>
			</div>

			<div v-if="!isUnlocked && showInfoBar" class="g-block-pay-audio-library__info">
				<SoundOutlined class="g-block-pay-audio-library__info-icon" />
				<span>
					可免费试听
					<strong>{{ freePreviewCount }}</strong>
					个，解锁后可收听全部
					<strong>{{ totalCount }}</strong>
					个音频
				</span>
			</div>

			<div class="g-block-pay-audio-library__grid" :style="{ gap: blockStyle.colGap }">
				<div v-for="entry in listEntries" :key="entry.key" class="g-block-pay-audio-library__item"
					@click.stop="onItemClick(entry, $event)">
					<div class="g-block-pay-audio-library__cover" :class="{
						'is-locked': entry.locked,
						'is-playable': isEntryPlayable(entry),
					}" :style="coverStyle">
						<img v-if="getCoverUrl(entry.item)" :src="getCoverUrl(entry.item)" alt="" />
						<i v-else class="mbm-iconfont mbm-yinpin"></i>
						<span v-if="!entry.locked" class="g-block-pay-audio-library__play" aria-hidden="true"
							role="button" :aria-label="isCurrentPlaying(entry) ? '暂停' : '播放'"
							@click.stop="onItemClick(entry, $event)">
							<span v-if="isCurrentPlaying(entry)" class="g-block-pay-audio-library__pause"></span>
							<span v-else class="g-block-pay-audio-library__triangle"></span>
						</span>
						<div v-if="entry.locked" class="g-block-pay-audio-library__lock-mask"
							:style="{ borderRadius: coverStyle.borderRadius }">
							<i class="mbm-iconfont mbm-suoding g-block-pay-audio-library__lock-icon"></i>
							<!-- <span class="g-block-pay-audio-library__lock-text">解锁全部音频</span> -->
						</div>
					</div>
					<div class="g-block-pay-audio-library__name" :style="{ color: libraryData.nameColor }">
						{{ getItemName(entry.item) }}
					</div>
				</div>
			</div>
		</div>

		<audio v-if="playingUrl" ref="playerEl" :src="playingUrl" autoplay @ended="onPlayEnded"></audio>
	</div>
</template>

<script>
import baseFunc from '../../../../config/base_func.js';
import { geditPayUnlockMixin } from '../../../mixins/geditPayUnlock.js';
import { SoundOutlined } from '@ant-design/icons-vue';
import {
	getAudioLibraryAudioUrl,
	getAudioLibraryBlockStyle,
	getAudioLibraryCoverStyle,
	getAudioLibraryCoverUrl,
	getAudioLibraryItemName,
} from '../../../utils/audioLibrary.js';
import {
	getPayAudioLibraryListEntries,
	isPayAudioLibraryUnlocked,
	normalizePayAudioLibraryData,
	setPayAudioLibraryUnlocked,
} from '../../../utils/payAudioLibrary.js';

export default {
	name: 'GPayAudioLibraryShell',
	mixins: [geditPayUnlockMixin],
	components: { SoundOutlined },
	props: {
		data: {
			type: Object,
			required: true,
		},
		blockId: {
			type: String,
			default: '',
		},
		editable: {
			type: Boolean,
			default: false,
		},
		active: {
			type: Boolean,
			default: false,
		},
		h5: {
			type: Boolean,
			default: false,
		},
		compact: {
			type: Boolean,
			default: false,
		},
		idKey: {
			type: String,
			default: '',
		},
	},
	emits: ['select'],
	data() {
		return {
			unlockedLocal: false,
			playingUrl: '',
			currentPlayingKey: '',
			permissionedUrl: '',
			permissionRequests: {},
		};
	},
	computed: {
		libraryData() {
			return normalizePayAudioLibraryData(this.data);
		},
		blockStyle() {
			return getAudioLibraryBlockStyle(this.libraryData, { h5: this.h5 });
		},
		coverStyle() {
			return getAudioLibraryCoverStyle(this.libraryData, { h5: this.h5, fixed: false });
		},
		buttonStyle() {
			return {
				background: this.libraryData.buttonBg,
			};
		},
		totalCount() {
			return this.libraryData.items.length;
		},
		freePreviewCount() {
			return Math.min(this.libraryData.freePreviewCount, this.totalCount);
		},
		subtitleText() {
			const subtitle = this.libraryData.subtitle || '';
			
			return subtitle;
		},
		isUnlocked() {
			if (this.editable) return false;
			return this.unlockedLocal || isPayAudioLibraryUnlocked(this.blockId) || this.isGeditModulePaid;
		},
		listEntries() {
			return getPayAudioLibraryListEntries(this.libraryData, {
				unlocked: this.isUnlocked,
				compact: this.compact,
			});
		},
		showInfoBar() {
			if (this.compact) return false;
			return this.freePreviewCount > 0 && this.freePreviewCount < this.totalCount;
		},
		isCurrentPlaying() {
			return (entry) => this.currentPlayingKey === entry.key;
		},
	},
	watch: {
		blockId: {
			immediate: true,
			handler() {
				this.syncUnlockState();
			},
		},
	},
	beforeUnmount() {
		this.stopPlay();
	},
	methods: {
		syncUnlockState() {
			this.unlockedLocal = isPayAudioLibraryUnlocked(this.blockId);
		},
		getCoverUrl(item) {
			return getAudioLibraryCoverUrl(item);
		},
		getItemName(item) {
			return getAudioLibraryItemName(item);
		},
		isEntryPlayable(entry) {
			if (!(this.editable || this.h5)) return false;
			// 编辑区可预览全部；H5 仅可播放未锁定条目
			return this.editable || !entry.locked;
		},
		onWrapClick() {
			if (this.editable) {
				this.$emit('select');
			}
		},
		onUnlockClick() {
			this.runGeditUnlockFlow({
				applyUnlock: () => {
					setPayAudioLibraryUnlocked(this.blockId, true);
					this.unlockedLocal = true;
				},
			});
		},
		onItemClick(entry, event) {
			if(entry.locked){
				this.onUnlockClick();
				return
			}

			if (!this.isEntryPlayable(entry)) return;
			event.stopPropagation();
			const url = getAudioLibraryAudioUrl(entry.item);
			if (!url) return;

			// 点击同一项则暂停
			if (this.currentPlayingKey === entry.key) {
				this.stopPlay();
				return;
			}

			const audioEl = this.$refs.playerEl;
			const entryKey = entry.key;

			if (this.h5 && entry.item.audio?.id && !this.permissionRequests[entryKey]) {
				this.permissionRequests[entryKey] = true;
				this.requestAudioPermission(entry.item.audio?.id, url, entryKey).catch(err => {
					this.permissionRequests[entryKey] = false;
					this.stopPlay();
					this.$message.error(err.data?.message || '请求失败');
				});
			} else {
				this.currentPlayingKey = entry.key;
				this.playingUrl = url;
				this.$nextTick(() => {
					audioEl?.play?.();
				});
			}
		},
		requestAudioPermission(itemId, url, key) {
			return this.$apiRequest.post('/api/getMediaPlayPermissions', {
				attach_id: itemId,
				id_key: this.idKey,
				unique: '',
				app_type: 1,
				p_key: this.getQueryStringByChannel('p_key') || '',
			}).then(res => {
				if (res.data?.play_url) {
					this.permissionedUrl = baseFunc.getQiniusign(res.data.play_url);
					this.playingUrl = this.permissionedUrl;
				} else {
					this.playingUrl = url;
				}
				this.currentPlayingKey = key;
				this.$nextTick(() => {
					this.$refs.playerEl?.play?.();
				});
			});
		},
		getQueryStringByChannel(name) {
			const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i');
			const query = window.location.href.split('?')[1];
			if (!query) return null;
			const re = query.match(reg);
			return re != null ? unescape(re[2]) : null;
		},
		onPlayEnded() {
			this.stopPlay();
		},
		stopPlay() {
			const el = this.$refs.playerEl;
			if (el) {
				el.pause();
			}
			this.playingUrl = '';
			this.currentPlayingKey = '';
			this.permissionedUrl = '';
		},
	},
};
</script>

<style scoped lang="less">
.g-block-pay-audio-library {
	box-sizing: border-box;

	&--edit {
		border: 1px solid transparent;
		border-radius: 4px;
		transition: border-color 0.2s, box-shadow 0.2s;
		cursor: pointer;

		&.is-active {
			border-color: #285af9;
			box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
		}
	}
}

.g-block-pay-audio-library__card {
	background: #fff;
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
}

.g-block-pay-audio-library__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 14px 10px 10px;
}

.g-block-pay-audio-library__header-main {
	flex: 1;
	min-width: 0;
}

.g-block-pay-audio-library__title {
	font-size: 18px;
	font-weight: 700;
	color: #1a1a1a;
	line-height: 1.35;
}

.g-block-pay-audio-library__subtitle {
	margin-top: 4px;
	font-size: 13px;
	color: #999;
	line-height: 1.4;
}

.g-block-pay-audio-library__header-action {
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
}

.g-block-pay-audio-library__price {
	display: flex;
	align-items: baseline;
	color: #ff4d4f;
	line-height: 1;
}

.g-block-pay-audio-library__currency {
	font-size: 14px;
	font-weight: 600;
	margin-right: 2px;
}

.g-block-pay-audio-library__price-value {
	font-size: 22px;
	font-weight: 700;
}

.g-block-pay-audio-library__unlock-btn {
	margin-top: 10px;
	margin-bottom: 4px;
	border: none;
	border-radius: 999px;
	padding: 10px 14px;
	font-size: 13px;
	font-weight: normal;
	color: #fff;
	cursor: pointer;
	white-space: nowrap;
	line-height: 1.3;

	&:active {
		opacity: 0.9;
	}
}

.g-block-pay-audio-library__info {
	display: flex;
	align-items: center;
	gap: 6px;
	margin: 0 10px 10px;
	padding: 8px 10px;
	background: #eef4ff;
	border-radius: 8px;
	font-size: 12px;
	color: #666;
	line-height: 1.5;

	strong {
		color: #285af9;
		font-weight: 600;
	}
}

.g-block-pay-audio-library__info-icon {
	flex-shrink: 0;
	font-size: 14px;
	color: #285af9;
}

.g-block-pay-audio-library__grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	padding: 0 10px 14px;
}

.g-block-pay-audio-library__item {
	min-width: 0;
}

.g-block-pay-audio-library__cover {
	position: relative;
	overflow: hidden;
	background: #f0f0f0;
	display: flex;
	align-items: center;
	justify-content: center;

	&.is-playable {
		cursor: pointer;
	}

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	i {
		font-size: 20px;
		color: #fff;
	}
}

.g-block-pay-audio-library__play {
	position: absolute;
	top: 50%;
	left: 50%;
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	margin: -18px 0 0 -18px;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.45);
	cursor: pointer;
}

.g-block-pay-audio-library__triangle {
	display: block;
	width: 0;
	height: 0;
	margin-left: 3px;
	border-top: 6px solid transparent;
	border-bottom: 6px solid transparent;
	border-left: 10px solid #fff;
}

.g-block-pay-audio-library__pause {
	display: block;
	width: 10px;
	height: 10px;
	border-left: 3px solid #fff;
	border-right: 3px solid #fff;
}

.g-block-pay-audio-library__lock-mask {
	position: absolute;
	inset: 0;
	z-index: 3;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 4px;
	background: rgba(0, 0, 0, 0.15);
}

.g-block-pay-audio-library__lock-icon {
	font-size: 20px;
	color: #fff;
}

.g-block-pay-audio-library__lock-text {
	font-size: 9px;
	color: #fff;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.g-block-pay-audio-library__cover.is-locked img {
	// filter: blur(6px);
	// transform: scale(1.08);
}

.g-block-pay-audio-library__name {
	margin-top: 8px;
	font-size: 12px;
	line-height: 1.4;
	text-align: center;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.g-block-pay-audio-library--h5 {
	.g-block-pay-audio-library__card {
		border-radius: 0.24rem;
		box-shadow: 0 0 0.24rem rgba(0, 0, 0, 0.1);
	}

	.g-block-pay-audio-library__header {
		gap: 0.24rem;
		padding: 0.28rem 0.2rem 0.2rem;
	}

	.g-block-pay-audio-library__title {
		font-size: 0.36rem;
	}

	.g-block-pay-audio-library__subtitle {
		margin-top: 0.08rem;
		font-size: 0.26rem;
	}

	.g-block-pay-audio-library__currency {
		font-size: 0.28rem;
	}

	.g-block-pay-audio-library__price-value {
		font-size: 0.44rem;
	}

	.g-block-pay-audio-library__unlock-btn {
		padding: 0.2rem 0.28rem;
		font-size: 0.26rem;
		margin-top: 0.2rem;
		margin-bottom: 0.08rem;
	}

	.g-block-pay-audio-library__info {
		gap: 0.12rem;
		margin: 0 0.2rem 0.2rem;
		padding: 0.16rem 0.2rem;
		border-radius: 0.16rem;
		font-size: 0.24rem;
	}

	.g-block-pay-audio-library__info-icon {
		font-size: 0.28rem;
	}

	.g-block-pay-audio-library__grid {
		padding: 0 0.2rem 0.28rem;
	}

	.g-block-pay-audio-library__cover i {
		font-size: 0.4rem;
	}

	.g-block-pay-audio-library__play {
		width: 0.72rem;
		height: 0.72rem;
		margin: -0.36rem 0 0 -0.36rem;
	}

	.g-block-pay-audio-library__triangle {
		margin-left: 0.06rem;
		border-top-width: 0.12rem;
		border-bottom-width: 0.12rem;
		border-left-width: 0.2rem;
	}

	.g-block-pay-audio-library__lock-icon {
		font-size: 0.24rem;
	}

	.g-block-pay-audio-library__lock-text {
		font-size: 0.18rem;
	}

	.g-block-pay-audio-library__lock-mask {
		gap: 0.08rem;
	}

	.g-block-pay-audio-library__name {
		margin-top: 0.16rem;
		font-size: 0.24rem;
	}
}
</style>
